import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CardSheetLayout, NavBar } from '@/components';
import { StepWizard } from '@/components/StepWizard';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import ROUTES from '@/router/routes';
import { KeysOf } from '@/types';
import { FADE_IN_OUT_LEFT, BACKGROUND_CLASS_SECTION_APP } from '@/utils';

export const WelcomeF5 = () => {
  const [steps, setSteps] = useState(0);
  const navigate = useNavigate();
  const { isMobile } = useIsDesktop();
  const { userInitialData } = useAuth();
  useMetaData({
    title: 'Mi Equipo de Fútbol 5',
  });
  if (userInitialData?.tiene_equipo_f5) {
    navigate(ROUTES.myTeam5, {
      replace: true,
    });
  }

  // nota: en realidad F5 podría terminar antes que el torneo general, pero
  // para simplificar la lógica, se toma esta condición (pq no se configura nunca de esa manera).
  const hasTournamentEnded =
    userInitialData?.veda &&
    userInitialData?.fechaActiva.id ===
      userInitialData?.todasLasFechas[
        userInitialData?.todasLasFechas.length - 1
      ]?.id;
  // const hasTournamentEnded =
  //  userInitialData?.estaPubLaUltJDeF5 && userInitialData?.veda;

  const dataComponent = useMemo(() => {
    if (hasTournamentEnded) {
      return [
        {
          title: 'LA COMPETENCIA HA FINALIZADO',
          subtitle:
            'Lamentablemente, ya no podés armar tu equipo. ¡Te esperamos en la próxima edición del juego que más te apasiona!',
          nameContext: 'welcomeF5_2' as KeysOf<
            typeof BACKGROUND_CLASS_SECTION_APP
          >,
        },
      ];
    }

    // Otherwise show the normal welcome flow
    return [
      {
        title: 'Armá tu equipo de Fútbol 5',
        subtitle:
          'Elegí un arquero y cuatro jugadores de campo, sin restricción de puesto y con presupuesto ilimitado. Siendo DT Premium o con un pin por fecha, tenés transferencias ilimitadas.',
        nameContext: 'welcomeF5_1' as KeysOf<
          typeof BACKGROUND_CLASS_SECTION_APP
        >,
      },
      {
        title: '¡Premios todas las fechas!',
        subtitle:
          'El ganador de cada fecha del juego se lleva $650.000. El campeón del torneo se lleva $3.000.000.',
        nameContext: 'welcomeF5_2' as KeysOf<
          typeof BACKGROUND_CLASS_SECTION_APP
        >,
      },
    ];
  }, [hasTournamentEnded, userInitialData?.tiene_equipo_f5]);

  const handleSubmit = () => {
    // Only navigate to build team if tournament hasn't ended
    if (!hasTournamentEnded) {
      navigate(ROUTES.buildTeam5);
    }
  };

  const contenMain = useMemo(() => {
    return (
      <CardSheetLayout
        footerComplex
        showFooter
        bgClassName={
          BACKGROUND_CLASS_SECTION_APP[dataComponent[steps].nameContext]
        }
        bgLabel="Jugador de fútbol pisando una pelota"
        cardClassName="pb-8 lg:pb-6 lg:pt-[75px] pt-14"
      >
        <StepWizard
          getCurrentStep={(activeStep: number) => setSteps(activeStep)}
          hideSteps={hasTournamentEnded}
          isControlled={hasTournamentEnded}
          onSubmit={handleSubmit}
        >
          {dataComponent.map(({ title, subtitle }) => {
            return (
              <motion.div
                key={steps}
                animate={'show'}
                initial="hide"
                variants={FADE_IN_OUT_LEFT}
              >
                <h1 className="text-gdt-gray uppercase font-normal font-bebas text-3xl text-center leading-8 lg:mt-[40px]">
                  {title}
                </h1>
                <p className="text-gdt-gray text-center font-medium leading-6 mt-[20px] mb-0 min-h-[108px] lg:mt-6 lg:mb-5 max-w-[318px] ml-auto mr-auto">
                  {subtitle}
                </p>
              </motion.div>
            );
          })}
        </StepWizard>
      </CardSheetLayout>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  if (isMobile) {
    return contenMain;
  }

  return <NavBar desktop>{contenMain}</NavBar>;
};
